import { useAuth } from "@/supabase-auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { asValidUuid } from "@/lib/uuidValidation";

/**
 * Query context types for explicit scope control
 * - 'user': Force user-scoped queries (profile pages)
 * - 'org': Force org-scoped queries (organization pages)
 * - 'auto': Automatic detection based on organization_id (legacy)
 */
export type QueryContext = 'user' | 'org' | 'auto';

/**
 * Hook to create user and organization-scoped query keys to prevent cache leakage between sessions
 * CRITICAL: Pass contextOverride='user' for profile pages to prevent org contamination
 * CRITICAL: Now validates UUIDs to prevent 400 errors from invalid ID formats
 */
export function useUserScopedQueryKey(contextOverride?: QueryContext) {
  const { session } = useAuth();
  const { profile } = useProfile();
  const { primaryRole } = useUserRole();
  
  // CRITICAL: Validate userId is a real UUID to prevent 400/401 cascades
  const userId = asValidUuid(session?.user?.id);
  
  // CRITICAL FIX: Allow explicit context override to separate profile/org data
  const effectiveContext = contextOverride || 'auto';
  
  // Determine organizationId based on context - validate as UUID
  const rawOrgId = effectiveContext === 'user' 
    ? null  // Force user-scoped queries - no org contamination
    : (profile?.organization_id ?? primaryRole?.organization_id);
  const organizationId = asValidUuid(rawOrgId);

  const createQueryKey = (baseKey: string | string[], ...additionalKeys: (string | number | undefined)[]): string[] => {
    const base = Array.isArray(baseKey) ? baseKey : [baseKey];
    
    let scopedKey: string[];
    
    if (effectiveContext === 'user' || (!organizationId && userId)) {
      // EXPLICIT user context OR fallback when no org
      scopedKey = ["user", userId, ...base];
    } else if (organizationId) {
      // Organization context
      scopedKey = ["org", organizationId, ...base];
    } else {
      // Anonymous/unauthenticated
      scopedKey = ["anonymous", ...base];
    }
    
    // Add any additional keys, filtering out undefined values
    const filteredAdditionalKeys = additionalKeys.filter(key => key !== undefined);
    
    return [...scopedKey, ...filteredAdditionalKeys.map(String)];
  };

  /**
   * Creates a typed tuple query key for stronger type inference
   * Filters out undefined parts to prevent unwanted query executions
   */
  const asQueryKeyTuple = <T extends readonly (string | number | undefined)[]>(
    baseKey: string | string[], 
    ...additionalKeys: T
  ): readonly string[] => {
    // Filter out undefined values before creating the key
    const validKeys = additionalKeys.filter((key): key is NonNullable<typeof key> => key !== undefined);
    return createQueryKey(baseKey, ...validKeys) as readonly string[];
  };

  const isAuthenticated = !!userId;
  const hasOrganization = !!organizationId;

  return {
    createQueryKey,
    asQueryKeyTuple,
    userId,
    organizationId: effectiveContext === 'user' ? null : organizationId,
    isAuthenticated,
    hasOrganization,
    effectiveContext
  };
}